// The result of a run on the canvas: Execute (every step and the standard answer) and Run all paths (every route).
import { Badge, JsonView } from '@/kit/ui'
import { pathOk, type FlowIssue, type FlowPath } from '@/lib/flow'
import type { ExecResult } from '@/lib/flowEngine'

export function ExecView({ result }: { result: ExecResult }) {
  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_360px]">
      <ol className="space-y-1 text-xs" data-testid="wf-steps">
        {result.steps.map((s, i) => (
          <li key={`${s.nodeId}-${i}`} className="rounded border border-slate-200 px-2 py-1 dark:border-slate-700">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-muted">{i + 1}</span>
              <b>{s.node}</b>
              {s.kind === 'answer' ? <Badge tone={['rejected', 'error', 'duplicate'].includes(s.port) ? 'danger' : 'success'}>answer {s.port}</Badge> : <code className={s.port === 'error' ? 'text-red-600' : ''}>→ {s.port}</code>}
              {s.ms > 0 && <span className="text-2xs text-muted">{s.ms} ms</span>}
            </div>
            {s.call && <div className="mono truncate text-muted" title={s.call.url}>{s.call.method} {s.call.url} · body {JSON.stringify(s.call.body).slice(0, 110)}</div>}
            {s.reply && <div className="text-2xs text-muted">reply: “{s.reply.name}” ({s.reply.status}){typeof s.value?.reply_code === 'string' ? ` · code ${s.value.reply_code}` : ''}</div>}
            {s.note && <div className="text-2xs text-muted">{s.note}</div>}
            {s.error && <div className="text-2xs text-red-600">{s.error}</div>}
          </li>
        ))}
        {result.stoppedBy !== 'answer' && <li className="text-xs text-red-600">Stopped: {result.stoppedBy === 'total-wait' ? `the total wait (${result.waitedS} s) is used up → pending` : result.stoppedBy === 'dead-end' ? 'a port with no line' : result.stoppedBy}</li>}
      </ol>
      <div>
        <div className="mb-1 text-2xs font-semibold">The standard answer the app would get · {result.ms} ms</div>
        <JsonView value={result.answer} maxHeight={300} />
      </div>
    </div>
  )
}

export function PathsView({ list, issues, recorded }: { list: FlowPath[]; issues: FlowIssue[]; recorded: boolean }) {
  const ok = list.filter(pathOk).length
  return (
    <div className="space-y-2 text-xs" data-testid="wf-paths-result">
      <p><b>{ok} / {list.length}</b> paths end at an Answer{issues.length ? `, ${issues.length} check(s) open` : ''}{ok === list.length && !issues.length ? ' → paths passed' : ''}{!recorded ? ' · not recorded on the version (unsaved changes or read-only): Save first' : ''}</p>
      {issues.length > 0 && <ul className="list-disc pl-5 text-red-700 dark:text-red-300">{issues.map((i, k) => <li key={k}>{i.text}</li>)}</ul>}
      <ul className="max-h-72 space-y-0.5 overflow-auto font-mono text-2xs">
        {list.map((p, i) => (
          <li key={i} className={pathOk(p) ? '' : 'text-red-600'}>
            {pathOk(p) ? '✓' : '✗'} {p.nodes.map((n, k) => (k < p.hops.length ? `${n} [${p.hops[k]}]` : n)).join(' → ')}{p.endsAt === 'poll' ? ' ↺ (poll)' : p.endsAt === 'dead-end' ? ' · no line' : p.endsAt === 'loop' ? ' · loop without a Wait' : ''}
          </li>
        ))}
      </ul>
    </div>
  )
}

